import React, { useEffect } from 'react'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    AllEvents,
    createEditionSelectItemEvent,
    createEditionViewItemListEvent,
    Product,
    WebLink,
} from '@news-mono/web-common'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionContent,
    StyledNightlyEditionPicture,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
    StyledNightlyEditionButtonContainer,
    Divider,
    StyledNightlyEditionLandingPageHeading,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import { HeaderContainer, HeaderPrefix } from './SpecialEdition.styled'

export interface SpecialEditionProps {
    title: string
    teaser: string
    link: string
    imageURL: string
    lazyloadImages?: boolean
    shouldShowAllEditions?: boolean
    alt: string
    publicationDate: string
    path: string
    onEvent?: (event: AllEvents) => void
    editionId?: string
}

export const SpecialEdition = ({
    title,
    teaser,
    link,
    imageURL,
    lazyloadImages,
    shouldShowAllEditions = false, // if false, it will show ReadEditionButton instead
    alt,
    path,
    onEvent,
    editionId,
}: SpecialEditionProps) => {
    const NightlyEditionHeading = shouldShowAllEditions
        ? StyledNightlyEditionLandingPageHeading
        : StyledNightlyEditionHeading

    useEffect(() => {
        if (shouldShowAllEditions) {
            return
        }
        const event = createEditionViewItemListEvent({
            editions: [
                {
                    id: editionId,
                    heading: teaser,
                    title: title,
                },
            ],
            collectionName: 'thenightly_on_home_page',
            itemCategory: 'thenightly_specific_edition_page',
        })

        if (onEvent && event) {
            onEvent(event)
        }
    }, [shouldShowAllEditions, editionId, teaser, title, onEvent])

    const handleClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition: {
                        id: editionId,
                        heading: title,
                    },
                    collectionName: 'TheNightlyOn_SpecialEditionImage',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: shouldShowAllEditions
                            ? 'thenightly_on_specific_edition_page'
                            : 'thenightly_on_home_page',

                        linkText: 'SpecialEditionImageClicked',
                        linkUrl: link,
                    },
                }),
            )
    }

    return (
        <>
            <StyledNightlyEditionContainer>
                <StyledNightlyEditionContent>
                    <StyledNightlyEditionPicture>
                        <WebLink to={link} onClick={handleClick}>
                            <img
                                loading={lazyloadImages ? 'lazy' : undefined}
                                src={imageURL}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = require('../../ePaper/assets/paper.jpg')
                                }}
                                alt={alt}
                            />
                        </WebLink>
                    </StyledNightlyEditionPicture>
                    <HeaderContainer>
                        <HeaderPrefix>The Nightly On</HeaderPrefix>
                        <NightlyEditionHeading>{title}</NightlyEditionHeading>
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainer>
                        <StyledNightlyEditionSubHeading>
                            In this Edition
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionButtonContainer>
                        <NightlyButton
                            variant={'default'}
                            text={'Read Edition'}
                            action={{
                                type: 'link',
                                to: link,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition: {
                                                id: editionId,
                                                heading: title,
                                            },
                                            collectionName:
                                                'TheNightlyOn_ReadEditionButton',
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: shouldShowAllEditions
                                                    ? 'thenightly_on_specific_edition_page'
                                                    : 'thenightly_on_home_page',

                                                linkText: 'Read Edition',
                                                linkUrl: link,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        {shouldShowAllEditions ? (
                            <NightlyButton
                                variant={'default'}
                                text={'All Editions'}
                                action={{
                                    type: 'link',
                                    to: '/on',
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition: {
                                                    id: editionId,
                                                    heading: title,
                                                },
                                                collectionName:
                                                    'TheNightlyOn_AllEditionsButton',
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category:
                                                        'thenightly_on_specific_edition_page',
                                                    linkText: 'All Editions',
                                                    linkUrl: '/on',
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        ) : (
                            <NightlyButton
                                variant={'default'}
                                text={'More Articles'}
                                action={{
                                    type: 'link',
                                    to: path,
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition: {
                                                    id: editionId,
                                                    heading: title,
                                                },
                                                collectionName:
                                                    'TheNightlyOn_MoreArticlesButton',
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category:
                                                        'thenightly_on_home_page',
                                                    linkText: 'More Articles',
                                                    linkUrl: path,
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        )}
                    </StyledNightlyEditionButtonContainer>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                </StyledNightlyEditionContent>
            </StyledNightlyEditionContainer>

            <Divider />
        </>
    )
}
